// AI 노트 «단어장» 창(클로바 묶음 C3b) — 내 단어장(편집) + 공용 단어장(관리자 지정·읽기 전용).
// 🔸 지금 STT 워커는 이 값을 아직 읽지 않는다(`services/meeting_settings.merged_glossary` 주석) →
//    화면 문구에 '받아쓰기에 쓰인다' 고 쓰지 않는다.
import { useEffect, useRef, useState } from 'react';
import { alertDialog, toast } from '../../components/AppDialog';
import { MEETING_THEME, softWrap } from '../../utils/meetingUtil';
import { detailOr, getGlossary, putGlossary } from './meetingWordsApi';

const T = MEETING_THEME;

const TERM_MAX = 50;              // 서버 GLOSSARY_TERM_MAX
const PERSONAL_MAX = 200;         // 서버 PERSONAL_GLOSSARY_MAX

export default function GlossaryDialog({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [personal, setPersonal] = useState([]);
  const [savedPersonal, setSavedPersonal] = useState([]);   // 서버에 있는 그대로(= 바뀜 판정 기준)
  const [shared, setShared] = useState([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { ok, data } = await getGlossary();
        if (!alive) return;
        if (!ok) {
          setLoadError('단어장을 불러오지 못했습니다.');
          return;
        }
        const mine = Array.isArray(data.personal) ? data.personal : [];
        setPersonal(mine);
        setSavedPersonal(mine);
        setShared(Array.isArray(data.shared) ? data.shared : []);
      } catch (e) {
        if (alive) setLoadError('단어장을 불러오지 못했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const dirty = personal.join('\n') !== savedPersonal.join('\n');

  const addTerm = (raw) => {
    const term = String(raw ?? '').trim();
    if (!term || personal.length >= PERSONAL_MAX) return;
    // 서버 `clean_glossary_terms` 와 같은 기준(대소문자 무시)으로 이미 있으면 더하지 않는다.
    const key = term.toLocaleLowerCase();
    if (personal.some((t) => String(t).trim().toLocaleLowerCase() === key)) return;
    setPersonal((arr) => [...arr, term]);
    setDraft('');
  };

  const draftKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // 한글 조합 중의 Enter 는 추가가 아니다(조합이 끝난 뒤 keydown 이 한 번 더 온다).
    if (e.nativeEvent?.isComposing) return;
    // 🔴 state 가 아니라 입력칸의 현재 값을 읽는다.
    addTerm(e.currentTarget.value);
  };

  const save = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      const { ok, data } = await putGlossary(personal);
      if (!ok) {
        alertDialog('단어장 저장 실패: ' + detailOr(data, '저장하지 못했습니다.'));
        return;
      }
      const mine = Array.isArray(data.personal) ? data.personal : personal;
      setPersonal(mine);
      setSavedPersonal(mine);
      if (Array.isArray(data.shared)) setShared(data.shared);
      toast('단어장을 저장했습니다.');
    } catch (e) {
      alertDialog('단어장 저장 실패: 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    maxWidth: '100%',
    padding: '3px 5px 3px 9px',
    borderRadius: T.radius.pill,
    border: `1px solid ${T.color.border}`,
    background: T.color.subtle,
    color: T.color.text,
    fontSize: T.font.sm,
    fontWeight: 700,
  };
  const sectionTitle = { fontSize: T.font.sm, fontWeight: 900, color: T.color.textStrong, marginBottom: '6px' };
  const canAdd = !!draft.trim() && personal.length < PERSONAL_MAX;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-label="단어장" onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '440px', background: '#fff', borderRadius: T.radius.lg, boxShadow: T.shadow.raised, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 12px', borderBottom: `1px solid ${T.color.border}`, background: T.color.subtle }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: T.font.lg, fontWeight: 900, color: T.color.textStrong }}>단어장</div>
          <button type="button" title="닫기" aria-label="닫기" onClick={onClose}
            style={{ border: 'none', background: 'transparent', padding: '2px 4px', color: T.color.muted, cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>
            ×
          </button>
        </div>
        <div style={{ maxHeight: '360px', overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontSize: T.font.sm, color: T.color.muted, lineHeight: 1.5, ...softWrap }}>자주 쓰는 단어를 등록해 두세요.</div>
          {loading && <div style={{ fontSize: T.font.base, color: T.color.faint }}>불러오는 중…</div>}
          {!loading && loadError && <div style={{ fontSize: T.font.base, color: '#b91c1c' }}>{loadError}</div>}
          {!loading && !loadError && (
            <>
              <div>
                <div style={sectionTitle}>내 단어장 <span style={{ fontWeight: 700, color: T.color.muted }}>{personal.length} / {PERSONAL_MAX}</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {personal.map((term) => (
                    <span key={term} style={chipStyle}>
                      <span style={{ minWidth: 0, ...softWrap }}>{term}</span>
                      <button type="button" onClick={() => setPersonal((arr) => arr.filter((t) => t !== term))}
                        title={`단어 삭제 ${term}`} aria-label={`단어 삭제 ${term}`}
                        style={{ width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: T.color.faint, cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>
                        ×
                      </button>
                    </span>
                  ))}
                  {personal.length === 0 && <span style={{ fontSize: T.font.base, color: T.color.faint }}>등록한 단어가 없습니다.</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <input ref={draftRef} aria-label="새 단어" maxLength={TERM_MAX} value={draft} placeholder="단어 입력 후 Enter"
                    onChange={(e) => setDraft(e.target.value)} onKeyDown={draftKeyDown}
                    disabled={personal.length >= PERSONAL_MAX}
                    style={{ width: '170px', boxSizing: 'border-box', fontSize: T.font.base, padding: '6px 9px', borderRadius: T.radius.md, border: `1px solid ${T.color.border}` }} />
                  <button type="button" onClick={() => addTerm(draftRef.current?.value ?? draft)} disabled={!canAdd}
                    style={{ fontSize: T.font.sm, fontWeight: 800, padding: '6px 10px', borderRadius: T.radius.md, border: `1px solid ${T.color.outlineBtnBorder}`, background: '#fff', color: T.color.textStrong, cursor: canAdd ? 'pointer' : 'not-allowed', opacity: canAdd ? 1 : 0.55 }}>
                    추가
                  </button>
                  <button type="button" onClick={save} disabled={saving || !dirty}
                    style={{ fontSize: T.font.sm, fontWeight: 800, padding: '6px 12px', borderRadius: T.radius.md, border: `1px solid ${T.color.outlineBtnBorder}`, background: '#fff', color: T.color.textStrong, cursor: (saving || !dirty) ? 'not-allowed' : 'pointer', opacity: (saving || !dirty) ? 0.55 : 1 }}>
                    {saving ? '저장 중…' : '저장'}
                  </button>
                </div>
              </div>
              <div>
                <div style={sectionTitle}>공용 단어장(관리자 지정)</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {shared.map((term) => (
                    <span key={term} style={{ ...chipStyle, padding: '3px 9px', color: T.color.muted }}>
                      <span style={{ minWidth: 0, ...softWrap }}>{term}</span>
                    </span>
                  ))}
                  {shared.length === 0 && <span style={{ fontSize: T.font.base, color: T.color.faint }}>관리자가 등록한 공용 단어가 없습니다.</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
